import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getProducts, getCategories } from '../api'
import { useCart } from '../context/CartContext'
import SocialProofNotification from '../components/notifications/SocialProofNotification'
import './Products.css'

export default function Products() {
    const [products, setProducts] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const { addToCart } = useCart()

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true)
                const [productsData, categoriesData] = await Promise.all([
                    getProducts({ category: selectedCategory, per_page: 100 }),
                    getCategories()
                ])
                setProducts(productsData.products || [])
                setCategories(categoriesData || [])
            } catch (err) {
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [selectedCategory])

    if (loading) {
        return (
            <div className="products-page">
                <div className="container">
                    <div className="loading">
                        <div className="spinner"></div>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="products-page">
                <div className="container">
                    <div className="error-state">
                        <h2>Unable to Load Products</h2>
                        <p>{error}</p>
                        <p className="text-muted">Make sure WordPress is running at localhost:8080</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="products-page">
            <div className="container">
                {/* Header */}
                <div className="products-header">
                    <h1>Our Products</h1>
                    <p className="text-secondary">
                        Discover our curated selection of premium products
                    </p>
                </div>

                {/* Category Filter */}
                {categories.length > 0 && (
                    <div className="category-filter">
                        <button
                            className={`filter-btn ${!selectedCategory ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(null)}
                        >
                            All
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                className={`filter-btn ${selectedCategory === cat.slug ? 'active' : ''}`}
                                onClick={() => setSelectedCategory(cat.slug)}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                )}

                {/* Products Grid */}
                {products.length === 0 ? (
                    <div className="empty-state">
                        <h3>No Products Found</h3>
                        <p>Products will appear here once added in WooCommerce.</p>
                    </div>
                ) : (
                    <div className="products-grid">
                        {products.map(product => {
                            // Ensure we have a valid slug, fallback to id if needed
                            const productLink = product.slug && product.slug !== '' ? product.slug : String(product.id)

                            return (
                            <article key={product.id} className="product-card card">
                                <Link to={`/products/${productLink}`} className="product-image">

                                    {product.images?.[0]?.src ? (
                                        <img src={product.images[0].src} alt={product.name} />
                                    ) : (
                                        <div className="product-placeholder">
                                            <span>📦</span>
                                        </div>
                                    )}
                                    {product.on_sale && <span className="sale-badge">Sale</span>}
                                </Link>
                                <div className="product-info">
                                    <Link to={`/products/${productLink}`}>
                                        <h3 className="product-title">{product.name}</h3>
                                    </Link>

                                    <div className="product-price">
                                        {product.on_sale && product.regular_price && (
                                            <span className="original-price">${product.regular_price}</span>
                                        )}
                                        <span className="current-price">${product.price}</span>
                                    </div>
                                    <button
                                        className="btn btn-primary btn-sm add-to-cart"
                                        onClick={() => addToCart(product)}
                                        disabled={!product.in_stock}
                                    >
                                        {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                                    </button>
                                </div>
                            </article>
                            )
                        })}
                    </div>
                )}

            </div>

            {/* Social Proof Notifications */}
            <SocialProofNotification />
        </div>
    )
}
